"use client";

/**
 * Testimonials.tsx
 *
 * Sliding testimonials section for the Royal Blue homepage.
 *
 * Shows 3 cards at a time on desktop and slides horizontally.
 * Navigation arrows appear on hover; dot pagination is always visible.
 *
 * Data comes from the Redux content store (CMS-driven).
 * Falls back to hard-coded reviews if CMS data is missing or invalid.
 *
 * Error safety:
 *  - TestimonialsErrorBoundary catches any render error so the section
 *    degrades gracefully without crashing the page.
 *  - getSafeReviews validates the CMS array before use.
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAppSelector } from "@/store/hooks";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FeatureErrorBoundary } from "@/Shared/ErrorBoundary";

import TestimonialCard, { type Testimonial } from "./TestimonialCard";

// ---------------------------------------------------------------------------
// Default content (fallback when CMS returns nothing)
// ---------------------------------------------------------------------------

const DEFAULT_BG =
  "https://images.unsplash.com/photo-1574031493431-921d3c728ec7?q=80&w=1170&auto=format&fit=crop";

const DEFAULT_REVIEWS: Testimonial[] = [
  {
    text: "Been to this stunning place many times over the last years. Tonight I have to say, it was good as it ever was. Superb food, exceedingly good staff.",
    authorName: "Sami Wade",
    authorRole: "Guest",
    rating: 5,
    avatar:
      "https://7oroof.com/demos/elroyale/assets/images/testimonials/thumbs/3.png",
  },
  {
    text: "Once again, I feel compelled to write about our wonderful dining experience. We have shared meals or conversations about our meals at your restaurant.",
    authorName: "Martin Hope",
    authorRole: "ProMov",
    rating: 5,
    avatar:
      "https://7oroof.com/demos/elroyale/assets/images/testimonials/thumbs/2.png",
  },
  {
    text: "Lovely atmosphere staff were excellent, very attentive and polite. We felt taken care of! Food came out quickly and was hot and delicious.",
    authorName: "John Peter",
    authorRole: "Guest",
    rating: 5,
    avatar:
      "https://7oroof.com/demos/elroyale/assets/images/testimonials/thumbs/1.png",
  },
  {
    text: "The wine selection is impeccable, and the steak was cooked to absolute perfection. A truly world-class dining experience in every way possible.",
    authorName: "Emily Chen",
    authorRole: "Food Critic",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2070&auto=format&fit=crop",
  },
  {
    text: "I booked a table for my anniversary, and they made it so special. The service was attentive without being intrusive. Simply wonderful atmosphere.",
    authorName: "Richard Roe",
    authorRole: "Regular",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop",
  },
  {
    text: "The desserts are a piece of art. I couldn't stop taking pictures. The flavor was even better than the presentation. Can't wait to return!",
    authorName: "Sarah Bell",
    authorRole: "Pastry Chef",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=1974&auto=format&fit=crop",
  },
];

// ---------------------------------------------------------------------------
// Data helpers
// ---------------------------------------------------------------------------

/** Filter the CMS testimonials array, keeping only structurally valid items */
function getSafeReviews(raw: unknown): Testimonial[] {
  try {
    if (!Array.isArray(raw) || raw.length === 0) return DEFAULT_REVIEWS;
    const valid = raw.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof item.text === "string" &&
        typeof item.authorName === "string",
    );
    return valid.length > 0 ? valid : DEFAULT_REVIEWS;
  } catch {
    return DEFAULT_REVIEWS;
  }
}

// ---------------------------------------------------------------------------
// Inner slider component
// ---------------------------------------------------------------------------

const TestimonialsSlider = () => {
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.testimonials;

  const backgroundImage =
    typeof data?.backgroundImage === "string" &&
    (data.backgroundImage.startsWith("http") ||
      data.backgroundImage.startsWith("data:"))
      ? data.backgroundImage
      : DEFAULT_BG;

  const subtitle =
    typeof data?.subtitle === "string" && data.subtitle.trim()
      ? data.subtitle
      : "Our Guestbook";
  const title =
    typeof data?.title === "string" && data.title.trim()
      ? data.title
      : "What People Say";
  const reviews = getSafeReviews(data?.testimonials);

  const [activeIndex, setActiveIndex] = useState(0);

  // We show 3 cards at once, so we can slide (reviews.length - 2) times
  const totalSlides = Math.max(1, reviews.length - 2);

  const goNext = () => setActiveIndex((prev) => (prev + 1) % totalSlides);
  const goPrev = () =>
    setActiveIndex((prev) => (prev - 1 + totalSlides) % totalSlides);

  return (
    <section className="relative py-24 md:py-32 overflow-hidden bg-black">
      {/* Background photo with dark gradient overlay */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center opacity-80 transition-transform duration-[30s] ease-linear scale-110"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/80 via-black/40 to-black/80" />

      <div className="relative z-10 container mx-auto px-6 max-w-7xl">
        {/* Section heading */}
        <div className="text-center mb-16">
          <motion.h4
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="font-sacramento text-3xl md:text-4xl text-[#c59d5f] mb-2 font-light italic"
          >
            {subtitle}
          </motion.h4>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-cormorant text-5xl md:text-6xl text-white mb-6 font-bold"
          >
            {title}
          </motion.h2>

          {/* Diamond divider */}
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-[1px] bg-[#c59d5f]/30" />
            <div className="flex gap-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rotate-45 border border-[#c59d5f]"
                />
              ))}
            </div>
            <div className="w-12 h-[1px] bg-[#c59d5f]/30" />
          </div>
        </div>

        {/* Sliding cards + navigation arrows */}
        <div className="relative group">
          <div className="overflow-hidden">
            <motion.div
              className="flex gap-8"
              animate={{
                x: `calc(-${activeIndex * 33.33}% - ${activeIndex * 1.33}rem)`,
              }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              {reviews.map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-full md:w-[calc(33.33%-1.33rem)]"
                >
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </motion.div>
          </div>

          {/* Previous / next arrows — shown only when there is more than one slide */}
          {totalSlides > 1 && (
            <>
              <button
                onClick={goPrev}
                className="absolute left-[-2rem] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#c59d5f] hover:border-[#c59d5f] transition-all opacity-0 group-hover:opacity-100 hidden lg:flex"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={goNext}
                className="absolute right-[-2rem] top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#c59d5f] hover:border-[#c59d5f] transition-all opacity-0 group-hover:opacity-100 hidden lg:flex"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>

        {/* Dot pagination */}
        {totalSlides > 1 && (
          <div className="flex justify-center gap-2 mt-16">
            {Array.from({ length: totalSlides }).map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`transition-all duration-300 ${
                  activeIndex === i
                    ? "bg-[#c59d5f] w-10 h-1.5 rounded-full"
                    : "bg-white/20 w-1.5 h-1.5 rounded-full hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Inline style for the decorative ribbon under each avatar */}
      <style>{`
        .clip-path-ribbon {
          clip-path: polygon(100% 0, 100% 100%, 50% 88%, 0 100%, 0 0);
        }
      `}</style>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Public export — always wrapped in the error boundary
// ---------------------------------------------------------------------------

const Testimonials = () => (
  <FeatureErrorBoundary featureName="Testimonials">
    <TestimonialsSlider />
  </FeatureErrorBoundary>
);

export default Testimonials;
